import React, {useState} from 'react';
import {Stack, FormControlLabel, RadioGroup} from '@mui/material';
import {
  Modal,
  Button,
  Headline,
  Body,
  designSystemToken,
} from '@lightricks/react-design-system';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {
  RESOLUTION_OPTIONS,
  TargetResolution,
  matchesAspectRatio,
  formatAspectRatio,
} from '@/types/postProductionGeneration';
import CheckmarkRadioButton from '@/components/checkmark-radio-button';
import FlashMessage from '@/components/flash-message';
import useResizeAnalytics from '@/hooks/analytics/use-resize-analytics';
import useStartResizeMutation from '@/hooks/mutations/useStartResizeMutation';

export type ResizeAspectRatioModalProps = {
  testID?: string;
  open: boolean;
  onClose: () => void;
  campaignId: string;
  assetId: string;
  creatorId?: string;
  remainingResizes?: number;
  resizeLimit?: number;
  sourceWidth?: number;
  sourceHeight?: number;
};

function ResizeAspectRatioModal({
  testID = 'resize-aspect-ratio-modal',
  open,
  onClose,
  campaignId,
  assetId,
  creatorId,
  remainingResizes,
  resizeLimit,
  sourceWidth,
  sourceHeight,
}: ResizeAspectRatioModalProps) {
  const currentAspectRatio =
    sourceWidth != null && sourceHeight != null
      ? formatAspectRatio(sourceWidth, sourceHeight)
      : null;
  const isCurrentRatio = (resolution: TargetResolution) =>
    sourceWidth != null &&
    sourceHeight != null &&
    matchesAspectRatio(resolution, sourceWidth, sourceHeight);
  const [targetResolution, setTargetResolution] =
    useState<TargetResolution | null>(null);
  const {trackStarted} = useResizeAnalytics({
    campaignId,
    creatorId,
    flowId: assetId,
  });

  const resetAndClose = () => {
    setTargetResolution(null);
    onClose();
  };

  const startResize = useStartResizeMutation({
    onSuccess: () => {
      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        message: (
          <FlashMessage.Banner
            title={translate('Resize started')}
            subtitle={translate(
              "We're resizing your video — it'll appear below when it's ready."
            )}
          />
        ),
      });
      resetAndClose();
    },
    onError: (_error, detail) => {
      raiseFlashMessageError({
        title: translate('Failed to start resizing'),
        subtitle: detail,
      });
    },
  });

  const handleClose = () => {
    if (startResize.isLoading) return;
    resetAndClose();
  };

  const handleSubmit = () => {
    if (!targetResolution) return;
    trackStarted(targetResolution);
    startResize.mutate({campaignId, assetId, targetResolution});
  };

  return (
    <Modal open={open} handleClose={handleClose} size="large" showCloseButton>
      <Stack
        gap="16px"
        padding="24px"
        alignItems="flex-start"
        data-testid={testID}
      >
        <Headline size="md">{translate('Resize video')}</Headline>
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate(
            "Choose an aspect ratio. We'll create a resized copy — your original stays unchanged."
          )}
        </Body>
        {remainingResizes != null && resizeLimit != null ? (
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {translate(
              '{remaining} of {max} resizes remaining on this campaign',
              {
                remaining: remainingResizes,
                max: resizeLimit,
              }
            )}
          </Body>
        ) : null}
        {currentAspectRatio ? (
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {translate('Current aspect ratio: {ratio}', {
              ratio: currentAspectRatio,
            })}
          </Body>
        ) : null}

        <RadioGroup
          value={targetResolution ?? ''}
          onChange={(_, v) => setTargetResolution(v as TargetResolution)}
        >
          {RESOLUTION_OPTIONS.map((option) => {
            const isCurrent = isCurrentRatio(option.resolution);
            return (
              <FormControlLabel
                key={option.resolution}
                value={option.resolution}
                disabled={isCurrent}
                control={<CheckmarkRadioButton />}
                label={
                  isCurrent
                    ? translate('{label} (current)', {
                        label: translate(option.label),
                      })
                    : translate(option.label)
                }
              />
            );
          })}
        </RadioGroup>

        <Stack
          direction="row"
          justifyContent="flex-end"
          gap="12px"
          sx={{width: '100%'}}
        >
          <Button
            mode="tinted"
            appearance="neutral"
            size="medium"
            onClick={handleClose}
            disabled={startResize.isLoading}
          >
            {translate('Cancel')}
          </Button>
          <Button
            mode="filled"
            appearance="brand"
            size="medium"
            onClick={handleSubmit}
            disabled={!targetResolution || startResize.isLoading}
          >
            {translate('Start resizing')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}

export default ResizeAspectRatioModal;
